import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../shared/components/Button/Button';
import Input from '../../../shared/components/Input/Input';
import Dialog from '../../../shared/components/Dialog/Dialog';
import { API_BASE_URL } from '../../../utils/apiClient';
import { uploadRHProfilePhoto } from '../rh.api';

const emptyProfile = { professionalTitle: '', headline: '', yearsOfExperience: '', mobility: '', availability: '', cvSummary: '', premiumSummary: '', linkedinUrl: '', portfolioUrl: '', photo: '' };
const listValue = (value) => Array.isArray(value) ? value.join(', ') : value || '';
const buildForm = (profile) => ({ ...emptyProfile, professionalTitle: profile?.professionalTitle ?? profile?.jobTitle ?? '', headline: profile?.headline ?? profile?.shortTitle ?? '', yearsOfExperience: profile?.yearsOfExperience ?? profile?.experienceYears ?? '', mobility: profile?.mobility ?? '', availability: profile?.availability ?? '', cvSummary: profile?.cvSummary ?? '', premiumSummary: profile?.premiumSummary ?? profile?.commercialSummary ?? '', linkedinUrl: profile?.linkedinUrl ?? '', portfolioUrl: profile?.portfolioUrl ?? '', photo: profile?.photo ?? profile?.profilePhotoPath ?? '', mainFields: listValue(profile?.mainFields), industries: listValue(profile?.industries), languages: listValue(profile?.languages) });
const absoluteImageUrl = (source) => !source || /^(?:blob:|data:|https?:)/i.test(source) ? source : source.startsWith('/') ? `${API_BASE_URL}${source}` : source;

function RHCareerProfileDialog({ open, profile, loading = false, error = '', onClose, onSubmit }) {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(() => buildForm(profile));
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = buildForm(profile);
    setForm(next);
    setPhotoFile(null);
    setPhotoPreview(absoluteImageUrl(next.photo));
    setUploadError('');
  }, [open, profile]);

  useEffect(() => {
    if (!photoFile) return undefined;
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const change = ({ target: { name, value } }) => setForm((current) => ({ ...current, [name]: value }));
  const selectPhoto = ({ target }) => {
    const file = target.files?.[0] || null;
    target.value = '';
    setUploadError('');
    if (!file) return setPhotoFile(null);
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      target.value = '';
      return setUploadError('Sélectionnez une photo JPEG, PNG ou WebP.');
    }
    if (file.size > 5 * 1024 * 1024) {
      target.value = '';
      return setUploadError('La photo ne doit pas dépasser 5 Mo.');
    }
    return setPhotoFile(file);
  };
  const submit = async () => {
    const toList = (value) => value.split(',').map((item) => item.trim()).filter(Boolean);
    setUploading(true);
    setUploadError('');
    try {
      let photo = form.photo;
      if (photoFile) photo = (await uploadRHProfilePhoto(photoFile)).photoPath;
      await onSubmit?.({ ...form, photo, yearsOfExperience: form.yearsOfExperience === '' ? null : Number(form.yearsOfExperience), mainFields: toList(form.mainFields), industries: toList(form.industries), languages: toList(form.languages) });
    } catch (failure) {
      setUploadError(failure.message || 'Le chargement de la photo a échoué.');
    } finally {
      setUploading(false);
    }
  };

  return <Dialog open={open} onClose={onClose} onSubmit={submit} title="Modifier ma présentation professionnelle" submitLabel="Enregistrer" loading={loading || uploading} size="large">
    {(error || uploadError) && <p className="rh-form-error" role="alert">{error || uploadError}</p>}
    <div className="custom-input custom-input--full rh-reference-image-field">
      <span className="custom-input__label">Photo professionnelle</span>
      <input ref={fileInputRef} hidden type="file" aria-label="Photo professionnelle" accept="image/jpeg,image/png,image/webp" disabled={loading || uploading} onChange={selectPhoto} />
      <Button variant="secondary" size="small" disabled={loading || uploading} onClick={() => fileInputRef.current?.click()} sx={{ alignSelf: 'flex-start' }}>Choisir une photo</Button>
      {photoFile && <span>{photoFile.name}</span>}
      <small>JPEG, PNG ou WebP · 5 Mo maximum</small>
      {photoPreview && <><img src={photoPreview} alt="Aperçu du profil professionnel" /><Button variant="tertiary" size="small" disabled={loading || uploading} sx={{ alignSelf: 'flex-start', color: 'var(--erp-danger-text)' }} onClick={() => { setPhotoFile(null); setPhotoPreview(''); setForm((current) => ({ ...current, photo: '' })); }}>Retirer la photo</Button></>}
    </div>
    <div className="rh-dialog-grid">
      <Input fullWidth label="Intitulé professionnel" name="professionalTitle" value={form.professionalTitle} onChange={change} />
      <Input fullWidth label="Titre court" name="headline" value={form.headline} onChange={change} />
      <Input fullWidth type="number" label="Années d’expérience" name="yearsOfExperience" value={form.yearsOfExperience} onChange={change} inputProps={{ min: 0, step: 0.5 }} />
      <Input fullWidth label="Mobilité" name="mobility" value={form.mobility} onChange={change} />
      <Input fullWidth label="Disponibilité" name="availability" value={form.availability} onChange={change} />
      <Input fullWidth label="Domaines principaux" name="mainFields" value={form.mainFields} onChange={change} helperText="Séparer par des virgules" />
      <Input fullWidth label="Secteurs d’activité" name="industries" value={form.industries} onChange={change} helperText="Séparer par des virgules" />
      <Input fullWidth label="Langues et niveaux" name="languages" value={form.languages} onChange={change} helperText="Exemple : Français : langue maternelle, Anglais : professionnel" />
      <Input fullWidth label="LinkedIn" name="linkedinUrl" value={form.linkedinUrl} onChange={change} />
      <Input fullWidth label="Portfolio" name="portfolioUrl" value={form.portfolioUrl} onChange={change} />
    </div>
    <h3 className="rh-dialog-subtitle">Présentation CV</h3>
    <Input fullWidth multiline rows={6} label="Résumé personnel et chronologique" name="cvSummary" value={form.cvSummary} onChange={change} />
    <h3 className="rh-dialog-subtitle">Présentation commerciale</h3>
    <Input fullWidth multiline rows={6} label="Expertise et valeur ajoutée pour une mission" name="premiumSummary" value={form.premiumSummary} onChange={change} />
  </Dialog>;
}

export default RHCareerProfileDialog;
